import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { CATALOG_URL, SITE_URL } from "../../config/site";
import ProductStructuredData from "../../components/ProductStructuredData";
import SEO from "../../components/SEO";
import { useCart } from "../../cart/CartContext";

import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";

import "./ProductPage.css";

export default function ProductPage() {
  const { slug } = useParams();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProduct() {
      try {
        const response = await fetch(CATALOG_URL);
        const data = await response.json();

        const foundProduct = data.products.find(
          (item) => item.slug === slug || item.id === slug
        );

        if (foundProduct) {
          setProduct(foundProduct);
          setSelectedImage(foundProduct.image || "");
          setQuantity(foundProduct.quantities?.[0] || 1);

          const initialOptions = {};

          if (foundProduct.options?.length) {
            foundProduct.options.forEach((option) => {
              initialOptions[option.id] = option.values?.[0]?.id || "";
            });
          }

          setSelectedOptions(initialOptions);
        }
      } catch (error) {
        console.error("Error loading product", error);
      } finally {
        setLoading(false);
      }
    }

    loadProduct();
  }, [slug]);

  const buildCartOptions = () => {
    if (!product.options?.length) {
      return {};
    }

    return product.options.reduce((options, option) => {
      const selectedValue = selectedOptions[option.id] || "";
      const selectedOptionValue = option.values?.find(
        (value) => value.id === selectedValue
      );

      return {
        ...options,
        [option.id]: {
          label: option.label,
          value: selectedValue,
          valueLabel: selectedOptionValue?.label || selectedValue,
        },
      };
    }, {});
  };

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      productName: product.name,
      category: product.category,
      type: product.type,
      quantity,
      unitPrice: product.price || 0,
      currency: product.currency || "EUR",
      options: buildCartOptions(),
      assets: [],
      notes: [],
    });

    setCheckoutMessage("Producto anadido al carrito.");
  };

  if (loading) {
    return (
      <>
        <Header />

        <main className="product-page">
          <p>Cargando producto...</p>
        </main>

        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <SEO
          title="Producto no encontrado | Imalandia"
          description="El producto solicitado no está disponible o la página no existe."
          canonical={`${SITE_URL}/producto/${slug}`}
        />

        <Header />

        <main className="product-page">
          <section className="product-not-found">
            <h1>Producto no encontrado</h1>

            <p>Este producto no está disponible o la página no existe.</p>

            <Link to="/">Volver al inicio</Link>
          </section>
        </main>

        <Footer />
      </>
    );
  }

  const gallery = product.gallery?.length
    ? product.gallery
    : product.image
      ? [product.image]
      : [];

  const totalPrice = (product.price || 0) * quantity;
  const hasOptions = product.options?.length > 0;
  const hasGallery = gallery.length > 1;

  const renderProductOptions = (className = "product-options") => {
    if (!hasOptions) return null;

    return (
      <div className={className}>
        {product.options.map((option) => (
          <label key={option.id} className="product-option">
            {option.label}

            <select
              value={selectedOptions[option.id] || ""}
              onChange={(e) => {
                const value = e.target.value;

                setSelectedOptions((prev) => ({
                  ...prev,
                  [option.id]: value,
                }));

                const optionImage =
                  product.optionImages?.[option.id]?.[value];

                if (optionImage) {
                  setSelectedImage(optionImage);
                }
              }}
            >
              {option.values.map((value) => (
                <option key={value.id} value={value.id}>
                  {value.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
    );
  };

  const renderGallery = (className = "product-gallery") => {
    if (!hasGallery) return null;

    return (
      <div className={className}>
        {gallery.map((image) => (
          <button
            key={image}
            type="button"
            className={
              image === selectedImage
                ? "product-gallery-thumb active"
                : "product-gallery-thumb"
            }
            onClick={() => setSelectedImage(image)}
          >
            <img
              src={image}
              alt={`${product.name} - imagen de producto`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <>
      <SEO
        title={product.seo?.title || `${product.name} | Imalandia`}
        description={
          product.seo?.description ||
          product.shortDescription ||
          product.description ||
          "Producto personalizado de Imalandia."
        }
        canonical={`${SITE_URL}/producto/${product.slug || product.id}`}
      />

      <ProductStructuredData product={product} />

      <Header />

      <main className="product-page">
        <section className="product-detail">
          <div className="product-detail-media">
            {selectedImage ? (
              <img
                src={selectedImage}
                alt={product.name || "Producto personalizado de Imalandia"}
                className="product-main-image"
              />
            ) : (
              <div className="product-image-placeholder">
                Imagen del producto
              </div>
            )}

            {renderProductOptions("product-options product-options-mobile")}

            {renderGallery("product-gallery product-gallery-mobile")}

            {renderGallery("product-gallery product-gallery-desktop")}
          </div>

          <div className="product-detail-content">
            <p className="product-eyebrow">Imalandia</p>

            <h1>{product.name}</h1>

            {product.description && (
              <p className="product-description">{product.description}</p>
            )}

            {!product.description && product.shortDescription && (
              <p className="product-description">{product.shortDescription}</p>
            )}

            {product.notice && (
              <div className="product-notice">{product.notice}</div>
            )}

            <div className="product-price">{totalPrice.toFixed(2)} €</div>

            {product.quantities?.length > 0 && (
              <label className="product-quantity">
                Cantidad

                <select
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                >
                  {product.quantities.map((qty) => (
                    <option key={qty} value={qty}>
                      {qty} unidad{qty > 1 ? "es" : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {renderProductOptions("product-options product-options-desktop")}

            <div className="product-checkout-box">
              {checkoutMessage && (
                <p className="product-checkout-message">{checkoutMessage}</p>
              )}

              <div className="product-actions">
                <button
                  type="button"
                  className="product-buy-button"
                  onClick={handleAddToCart}
                >
                  Anadir al carrito
                </button>

                <Link to="/carrito" className="product-back-link">
                  Ver carrito
                </Link>

                <Link to="/#tienda" className="product-back-link">
                  Volver a la tienda
                </Link>
              </div>
            </div>

            {product.features?.length > 0 && (
              <div className="product-features">
                <h2>Características</h2>

                <ul>
                  {product.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

            {product.related && (
              <div className="product-related-box">
                <h2>{product.related.title}</h2>

                <p>{product.related.text}</p>

                {product.related.primaryLink && (
                  <a href={product.related.primaryLink.url}>
                    {product.related.primaryLink.label}
                  </a>
                )}
              </div>
            )}

            {product.placement?.standOptional && (
              <div className="product-stand-box">
                <h2>
                  {product.placement.standTitle ||
                    "¿Quieres colocarlo fuera de la nevera?"}
                </h2>

                <p>
                  {product.placement.standText ||
                    "Puedes añadir un soporte opcional para colocarlo en estanterías, escritorios o muebles."}
                </p>

                {product.placement.standProductId && (
                  <a href={`/producto/${product.placement.standProductId}`}>
                    {product.placement.standLinkLabel || "Ver soporte opcional"}
                  </a>
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
